import { useFlag } from '@calendly/feature-check-react'
import { useMemo } from 'react'

import { useEventType } from 'features/event_type_provider'

import { getWorkflowsOwner } from './get_workflows_owner'
import { useFetchLegacyWorkflowsForEventTypeQuery, useFetchWorkflowsForEventTypeQuery } from '../../../api'
import { useEventTypeEditorSidebar } from '../../../hooks'

export const useWorkflows = ({ withAdminManaged = false } = {}) => {
  const workflowsExtractionEnabled = useFlag('raven_workflows_extraction')

  const { id: eventTypeId, creator_id: creatorId, isMETParent, parent_id: parentId, profile } = useEventType()
  const { type, ownerType: sidebarOwnerType, ownerId: sidebarOwnerId } = useEventTypeEditorSidebar()

  // The full-page CRUD editor is not opened through the right-rail sidebar, so its
  // paneProps (ownerType/ownerId) are absent (undefined). Fall back to the event
  // type's own profile owner so workflows ownership (canManageWorkflows) resolves
  // correctly instead of querying with an undefined owner and showing the owner-only
  // banner. Only fall back on undefined so an explicit sidebar null is preserved.
  const ownerType = sidebarOwnerType === undefined ? profile?.owner_type : sidebarOwnerType
  const ownerId = sidebarOwnerId === undefined ? profile?.owner_id : sidebarOwnerId
  const { id: workflowOwnerId, type: workflowOwnerType } = getWorkflowsOwner({ ownerType, ownerId, creatorId })

  const queryParams = useMemo(() => {
    // For managed event types,
    //   we need to ensure the query runs even without traditional creator/owner setup
    //   and only include owner info if available
    if (isMETParent) {
      return {
        eventTypeId,
        isMETParent,
        parentId,
        type,
        ...(workflowOwnerId && {
          ownerId: workflowOwnerId,
          ownerType: workflowOwnerType
        }),
        withAdminManaged
      }
    }

    return {
      eventTypeId,
      isMETParent,
      parentId,
      type,
      ownerId: workflowOwnerId,
      ownerType: workflowOwnerType,
      withAdminManaged
    }
  }, [
    eventTypeId,
    isMETParent,
    parentId,
    type,
    workflowOwnerId,
    workflowOwnerType,
    withAdminManaged
  ])

  const { data: workflowsData } = useFetchWorkflowsForEventTypeQuery(queryParams, {
    skip: !workflowsExtractionEnabled || !queryParams,
    refetchOnMountOrArgChange: true
  })
  const { data: legacyWorkflowsData } = useFetchLegacyWorkflowsForEventTypeQuery(queryParams, {
    skip: workflowsExtractionEnabled || !queryParams,
    refetchOnMountOrArgChange: true
  })

  return useMemo(() => (
    workflowsData || legacyWorkflowsData || { list: [], canManageWorkflows: false }
  ), [workflowsData, legacyWorkflowsData])
}
